import { HStack } from "@chakra-ui/layout";
import type { TasksQuery } from "@src/__generated__/graphql";
import { useUpdateTaskPriorityMutation } from "@src/__generated__/urql-graphql";
import type { InternalTaskType } from "@src/components/modules/resource-planning/tasks/listing/store";
import { ExternalAccessIcon } from "@src/components/widgets/ExternalAccessIcon";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { Fragment, type FunctionComponent } from "react";
import { TaskPriorityBadge } from "../TaskPriorityBadge";
import { TaskItemBase } from "./TaskItemBase";
import {
  TaskAvatars,
  TaskDeadline,
  TaskPlannedBudgetedTime,
} from "./TaskItemComponents";

export type TaskType = NonNullable<TasksQuery["tasks"]>["data"][0];

type TTaskItemProject = {
  task: InternalTaskType;
  onClick?: (task: InternalTaskType) => void;
  onCreateSubtask?: (task: InternalTaskType) => void;
  isProjectTemplate?: boolean;
  onCheck?: (taskId: InternalTaskType["id"]) => void;
  isChecked?: boolean;
  isIndeterminate?: boolean;
  checkedSubtaskIds?: InternalTaskType["id"][];
};

export const TaskItemProject: FunctionComponent<TTaskItemProject> = observer(
  function TaskItemProject({
    task,
    isProjectTemplate,
    onClick,
    onCreateSubtask,
    onCheck,
    isChecked,
    isIndeterminate,
    checkedSubtaskIds,
  }) {
    const { workspaceStore } = useStore();
    const [{ fetching: updatingPriority }, updatePriority] =
      useUpdateTaskPriorityMutation();

    return (
      <TaskItemBase
        onClick={onClick}
        task={task}
        title={task.name}
        showSubtasksToggle={false}
        subtasksVisible
        onCreateSubtask={onCreateSubtask}
        onCheck={onCheck}
        isChecked={isChecked}
        checkedSubtaskIds={checkedSubtaskIds}
        isIndeterminate={isIndeterminate}
        wrapperStyle={{
          gridTemplateColumns: {
            base: "7rem 5rem 1fr max-content 21% repeat(2, 14rem) 6rem",
            // eslint-disable-next-line lingui/no-unlocalized-strings
            "2xl": "7rem 5rem 1fr max-content 29% repeat(2, 14rem) 6rem",
          },
        }}
        content={(task) => (
          <Fragment>
            <TaskDeadline deadline={task.deadline} />
            <HStack justify="space-between">
              <TaskPlannedBudgetedTime
                plannedTime={task.stats?.planned_time ?? null}
                spentTime={task.stats?.spent_time ?? null}
                projectId={task.ourWorkBudgetItem?.project.id}
                budgetItemId={task.ourWorkBudgetItem?.id}
                taskId={task.id}
                parentTaskId={task.parent?.id}
                usesBudgetovac={task.ourWorkBudgetItem?.project.uses_budgetovac}
                isProjectTemplate={isProjectTemplate}
              />
              <ExternalAccessIcon
                type="task"
                id={task.id}
                scopes={task.scopes}
                isTemplate={task.is_template}
              />
            </HStack>

            <TaskPriorityBadge
              priority={task.priority}
              minW="80px"
              isEditable
              isLoading={updatingPriority}
              taskPriorities={workspaceStore.taskPriorities}
              onChange={async (newPriorityId) => {
                const { data, error } = await updatePriority({
                  task_id: task.id,
                  priority_id: newPriorityId,
                });

                if (!data || error) return;
                task.priority = data.updateTaskPriority.priority;
              }}
            />

            <TaskAvatars positions={task.positions} />
          </Fragment>
        )}
      />
    );
  },
);
